import React, { useState, useRef } from 'react';
import {
  uploadImage,
  upscaleImage,
  downloadImage,
  createMockUpscaledImage,
  formatFileSize,
  estimateUpscaledSize,
} from '../../services/imageUpscaler';

const ImageUpscaler = () => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [upscaleOptions, setUpscaleOptions] = useState({
    scale: 2,
    format: 'png',
    quality: 'high',
  });
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [upscaleProgress, setUpscaleProgress] = useState(0);
  const [upscaledResult, setUpscaledResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [compareSliderPosition, setCompareSliderPosition] = useState(50);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const compareContainerRef = useRef(null);

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      await handleImageUpload(files[0]);
    }
  };

  const handleFileInputChange = async (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      await handleImageUpload(files[0]);
    }
  };

  const handleImageUpload = async (file) => {
    setError(null);
    try {
      const imageData = await uploadImage(file);
      setUploadedImage(imageData);
      setUpscaledResult(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpscaleClick = async () => {
    if (!uploadedImage) return;

    setIsUpscaling(true);
    setError(null);
    setUpscaleProgress(0);

    try {
      const result = await upscaleImage({
        imageId: uploadedImage.id,
        scale: upscaleOptions.scale,
        format: upscaleOptions.format,
        quality: upscaleOptions.quality,
        onProgress: (progress) => {
          setUpscaleProgress(progress.progress);
        },
      });

      // Create mock upscaled image using canvas
      const upscaledDataUrl = await createMockUpscaledImage(
        uploadedImage.dataUrl,
        upscaleOptions.scale,
        upscaleOptions.format
      );

      setUpscaledResult({
        ...result,
        dataUrl: upscaledDataUrl,
        width: uploadedImage.width * upscaleOptions.scale,
        height: uploadedImage.height * upscaleOptions.scale,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUpscaling(false);
      setUpscaleProgress(0);
    }
  };

  const handleDownloadUpscaled = () => {
    if (!upscaledResult) return;

    const filename = `upscaled_${upscaleOptions.scale}x_${uploadedImage.name.split('.')[0]}.${upscaleOptions.format}`;
    downloadImage(upscaledResult.dataUrl, filename);
  };

  const handleNewUpload = () => {
    setUploadedImage(null);
    setUpscaledResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCompareSlider = (e) => {
    if (!compareContainerRef.current) return;

    const rect = compareContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setCompareSliderPosition(percentage);
  };

  const handleCompareSliderTouch = (e) => {
    if (!compareContainerRef.current) return;

    const touch = e.touches[0];
    const rect = compareContainerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setCompareSliderPosition(percentage);
  };

  return (
    <div className="max-w-[1200px] mx-auto">
      {!uploadedImage ? (
        // Upload section
        <section className="py-5">
          <div
            className={`bg-white border-[3px] border-dashed rounded-2xl py-[60px] px-10 text-center cursor-pointer transition-all ${
              dragActive
                ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="text-6xl mb-5">📁</div>
            <h3 className="text-2xl text-gray-900 mb-2">이미지를 업로드하세요</h3>
            <p className="text-base text-gray-500 mb-5">이미지를 드래그하거나 클릭하여 선택하세요</p>
            <div className="flex gap-5 justify-center mt-5">
              <span className="text-sm text-gray-500">지원 형식: JPG, PNG, WebP</span>
              <span className="text-sm text-gray-500">최대 크기: 10MB</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 px-4 bg-red-50 text-red-700 rounded-lg my-4 text-sm">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}
        </section>
      ) : (
        <>
          {/* Options and preview section */}
          <section className="grid grid-cols-2 gap-6 mb-6 md:grid-cols-1">
            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
              <h3 className="text-lg text-gray-900 mb-5">업스케일 옵션</h3>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">확대 배율</label>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-lg cursor-pointer transition-all ${
                      upscaleOptions.scale === 2
                        ? 'bg-blue-500 text-white border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, scale: 2 })}
                    disabled={isUpscaling}
                  >
                    2x
                  </button>
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-lg cursor-pointer transition-all ${
                      upscaleOptions.scale === 4
                        ? 'bg-blue-500 text-white border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, scale: 4 })}
                    disabled={isUpscaling}
                  >
                    4x
                  </button>
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">출력 포맷</label>
                <div className="flex gap-2">
                  {['png', 'jpg', 'webp'].map((format) => (
                    <button
                      key={format}
                      className={`flex-1 p-3 text-base font-semibold rounded-lg cursor-pointer transition-all ${
                        upscaleOptions.format === format
                          ? 'bg-blue-500 text-white border-2 border-blue-500'
                          : 'bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      onClick={() => setUpscaleOptions({ ...upscaleOptions, format })}
                      disabled={isUpscaling}
                    >
                      {format.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2.5">처리 품질</label>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-lg cursor-pointer transition-all ${
                      upscaleOptions.quality === 'fast'
                        ? 'bg-blue-500 text-white border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, quality: 'fast' })}
                    disabled={isUpscaling}
                  >
                    빠른 처리
                  </button>
                  <button
                    className={`flex-1 p-3 text-base font-semibold rounded-lg cursor-pointer transition-all ${
                      upscaleOptions.quality === 'high'
                        ? 'bg-blue-500 text-white border-2 border-blue-500'
                        : 'bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => setUpscaleOptions({ ...upscaleOptions, quality: 'high' })}
                    disabled={isUpscaling}
                  >
                    고품질
                  </button>
                </div>
              </div>

              <div className="bg-gray-100 rounded-lg p-4 my-5">
                <div className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                  <span className="text-sm text-gray-500">원본 크기:</span>
                  <span className="text-sm font-semibold text-gray-900">{uploadedImage.width} x {uploadedImage.height}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                  <span className="text-sm text-gray-500">출력 크기:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {uploadedImage.width * upscaleOptions.scale} x {uploadedImage.height * upscaleOptions.scale}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200 last:border-b-0">
                  <span className="text-sm text-gray-500">파일 크기:</span>
                  <span className="text-sm font-semibold text-gray-900">{formatFileSize(uploadedImage.size)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-500">예상 크기:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatFileSize(estimateUpscaledSize(uploadedImage.size, upscaleOptions.scale))}
                  </span>
                </div>
              </div>

              <div className="flex gap-2.5">
                <button
                  className="flex-1 p-3.5 text-base font-semibold border-none rounded-lg cursor-pointer transition-all bg-gradient-to-br from-blue-500 to-purple-500 text-white hover:enabled:-translate-y-0.5 hover:enabled:shadow-[0_4px_12px_rgba(0,122,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpscaleClick}
                  disabled={isUpscaling}
                >
                  {isUpscaling ? '처리 중...' : '업스케일 시작'}
                </button>
                <button
                  className="flex-1 p-3.5 text-base font-semibold rounded-lg cursor-pointer transition-all bg-gray-100 text-gray-900 border-2 border-gray-200 hover:enabled:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleNewUpload}
                  disabled={isUpscaling}
                >
                  새 이미지
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)]">
              <h3 className="text-lg text-gray-900 mb-5">미리보기</h3>
              <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-video flex items-center justify-center">
                <img
                  src={uploadedImage.dataUrl}
                  alt="Original"
                  className="max-w-full max-h-full object-contain"
                />
                <div className="absolute top-3 left-3 px-3 py-1.5 bg-black/70 text-white text-xs rounded-md">원본 이미지</div>
              </div>
            </div>
          </section>

          {/* Progress section */}
          {isUpscaling && (
            <section className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6 text-center">
              <h3 className="text-lg text-gray-900 mb-5">이미지 업스케일링 중...</h3>
              <div className="w-full h-2 bg-gray-200 rounded overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-[width] duration-300"
                  style={{ width: `${upscaleProgress}%` }}
                ></div>
              </div>
              <p className="text-xl font-semibold text-blue-500">{Math.round(upscaleProgress)}%</p>
            </section>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center gap-2 p-3 px-4 bg-red-50 text-red-700 rounded-lg my-4 text-sm">
              <span className="text-lg">⚠️</span>
              {error}
            </div>
          )}

          {/* Result section */}
          {upscaledResult && (
            <section className="bg-white rounded-2xl p-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)] mb-6">
              <h2 className="text-xl text-gray-900 mb-5 text-center">업스케일 완료!</h2>

              {/* Before/After comparison slider */}
              <div
                className="mb-6"
                ref={compareContainerRef}
                onMouseMove={handleCompareSlider}
                onTouchMove={handleCompareSliderTouch}
              >
                <div className="relative w-full aspect-video bg-gray-900 rounded-xl overflow-hidden cursor-ew-resize select-none">
                  <img
                    src={upscaledResult.dataUrl}
                    alt="Upscaled"
                    className="absolute top-0 left-0 w-full h-full object-contain"
                  />
                  <div
                    className="absolute top-0 left-0 h-full overflow-hidden"
                    style={{ width: `${compareSliderPosition}%` }}
                  >
                    <img
                      src={uploadedImage.dataUrl}
                      alt="Original"
                      className="absolute top-0 left-0 w-full h-full object-contain"
                      style={{ width: `${100 / (compareSliderPosition / 100)}%` }}
                    />
                  </div>
                  <div
                    className="absolute top-0 h-full w-0.5 bg-white -translate-x-1/2 pointer-events-none"
                    style={{ left: `${compareSliderPosition}%` }}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-[0_2px_10px_rgba(0,0,0,0.3)]">
                      <span className="text-xl text-gray-900">⟷</span>
                    </div>
                  </div>
                  <div className="absolute top-3 left-3 right-3 flex justify-between pointer-events-none">
                    <span className="px-3 py-1.5 bg-black/70 text-white text-xs rounded-md">원본</span>
                    <span className="px-3 py-1.5 bg-black/70 text-white text-xs rounded-md">업스케일</span>
                  </div>
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">
                  슬라이더를 좌우로 드래그하여 비교하세요
                </p>
              </div>

              {/* Result info */}
              <div className="mt-6">
                <div className="grid grid-cols-4 gap-3 mb-5 md:grid-cols-2">
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <span className="block text-xs text-gray-500 mb-1">확대 배율</span>
                    <span className="block text-base font-semibold text-gray-900">{upscaledResult.scale}x</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <span className="block text-xs text-gray-500 mb-1">원본 크기</span>
                    <span className="block text-base font-semibold text-gray-900">{uploadedImage.width} x {uploadedImage.height}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <span className="block text-xs text-gray-500 mb-1">출력 크기</span>
                    <span className="block text-base font-semibold text-gray-900">{upscaledResult.width} x {upscaledResult.height}</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <span className="block text-xs text-gray-500 mb-1">출력 포맷</span>
                    <span className="block text-base font-semibold text-gray-900">{upscaledResult.format.toUpperCase()}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    className="flex-1 p-3.5 text-base font-semibold border-none rounded-lg cursor-pointer transition-all bg-gradient-to-br from-blue-500 to-purple-500 text-white hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,122,255,0.3)]"
                    onClick={handleDownloadUpscaled}
                  >
                    다운로드
                  </button>
                  <button
                    className="flex-1 p-3.5 text-base font-semibold rounded-lg cursor-pointer transition-all bg-gray-100 text-gray-900 border-2 border-gray-200 hover:bg-gray-200"
                    onClick={handleNewUpload}
                  >
                    새 이미지 처리
                  </button>
                </div>

                {upscaledResult.message && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center text-sm text-blue-600">
                    i {upscaledResult.message}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Feature info */}
          {!upscaledResult && !isUpscaling && (
            <section className="bg-gray-100 rounded-2xl p-6 mt-6">
              <h3 className="text-base text-gray-900 mb-4">업스케일링 기능</h3>
              <div className="flex gap-4 md:flex-col">
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">1</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">AI 분석</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">이미지의 패턴과 디테일을 분석합니다.</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">2</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">해상도 향상</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">AI가 누락된 픽셀을 지능적으로 생성합니다.</p>
                  </div>
                </div>
                <div className="flex-1 flex gap-3 items-start">
                  <span className="w-7 h-7 bg-gradient-to-br from-blue-500 to-purple-500 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">3</span>
                  <div>
                    <h4 className="text-sm text-gray-900 mb-1">품질 최적화</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">선명도와 노이즈를 최적화하여 완성합니다.</p>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default ImageUpscaler;
